/**
 * Member onboarding — shared contract.
 *
 * This file is the single source of truth that the MemberService, the three
 * wizard step components and the parent stepper all import from, so the form
 * control names, request/response shapes and validation regexes stay aligned.
 *
 * Property names on the wire are camelCase (the API is case-insensitive on input).
 */

/** Normalized <select> option — same shape DropdownService produces. */
export interface DropdownItem {
  value: number | string;
  label: string;
}

/**
 * A premium rule from GET /api/PremiumRule/GetForCurrentUser.
 *
 * The API returns PascalCase keys; the service normalizes them to this
 * camelCase shape. Selecting a rule drives the read-only policy display, the
 * member's allowed age range and the live premium preview.
 */
export interface PremiumRule {
  premiumRuleId: number;
  masterPolicyName: string;
  coverageType: string;
  premiumCode: string;
  ageFrom: number;
  ageTo: number;
  basePremium: number;
  gstPercentage: number;
  doctorConsultant: number;
}

/** Standard API response envelope used across the app: branch on `success`. */
export interface ApiEnvelope<T = any> {
  success: boolean;
  message: string;
  statusCode: number;
  data: T;
}

// ===================== request shapes =====================

/** One nominee row (FormArray entry in Step 2). */
export interface NomineeRequest {
  nomineeName: string;
  dateOfBirth: string; // ISO yyyy-MM-dd
  relationship: string;
  nominationPercentage: number;
  address: string;
  mobileNumber: string;
  email?: string | null;
}

/**
 * POST /api/Member/Save body.
 *
 * Everything server-set is intentionally absent: PartnerId (derived from the
 * JWT), MasterPolicyName / MasterPolicyOnLoan, MemberUniqueId / MemberSeriesNo,
 * SumInsured and the whole premium breakup (the backend recomputes it from the
 * rule + isDoctorConsultant). Only the rule id, the member fields, the doctor
 * flag and the nominees go up.
 */
export interface SaveMemberRequest {
  primiumrulrID: number; // selected PremiumRuleId (backend spelling, keep as-is)
  firstName: string;
  middleName?: string | null;
  lastName?: string | null;
  loanNumber?: string | null; // blank => backend auto-generates
  dateOfBirth: string; // ISO yyyy-MM-dd
  age?: number | null;
  gender?: string | null;
  panNumber?: string | null;
  aadhaarNumber?: string | null;
  email?: string | null;
  mobileNumber: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  cflBranch?: string | null;
  healthDeclaration: boolean; // mandatory acknowledgement
  termcondition: boolean;
  isDoctorConsultant: boolean; // drives the backend premium calc
  nominees: NomineeRequest[];
}

/** POST /api/Member/VerifyOtp body. */
export interface VerifyOtpRequest {
  memberId: number;
  code: string;
}

/** POST /api/Member/ResendOtp body. */
export interface ResendOtpRequest {
  memberId: number;
}

// ===================== response shapes =====================

export interface OtpMeta {
  memberId: number;
  channels: string[]; // e.g. ["SMS", "EMAIL"]
  expiresInMinutes: number;
}

/** data payload of a successful POST /api/Member/Save. */
export interface MemberSaveData {
  memberId: number;
  memberUniqueId: string;
  memberSeriesNo?: string; // e.g. MEM-2026-27-0000001 (server-generated)
  masterPolicyOnLoan?: string; // e.g. 123/M (server-generated)
  otpSent: boolean;
  otp: OtpMeta;
  otpMessage: string;
}

/** data payload of a successful POST /api/Member/VerifyOtp. */
export interface VerifyOtpData {
  memberId: number;
  kycStatus: string; // "VERIFIED"
}

// ===================== shared constants =====================

/** Validation regexes — reused by every step so the rules never drift. */
export const MEMBER_PATTERNS = {
  pan: /^[A-Z]{5}[0-9]{4}[A-Z]$/,
  aadhaar: /^\d{12}$/, // exactly 12 digits
  // Indian mobile numbers are 10 digits starting 6-9. Plain \d{10} accepted "0000000000",
  // which both APIs now reject — keep the three in step so the wizard never posts a number
  // the server will refuse.
  mobile: /^[6-9]\d{9}$/,
  pincode: /^\d{6}$/, // exactly 6 digits
} as const;

/**
 * Completed years between a date of birth and today.
 *
 * One definition, used by the field that DISPLAYS the age, the validator that CHECKS it against
 * the plan's band, and the bounds that stop an ineligible date being picked at all. Two of those
 * disagreeing by a day is a member who is told they are eligible and then refused on save — which
 * is what the server, computing this same way, would do.
 *
 * Returns null for an absent or unparseable date.
 */
export function ageOnToday(dob: unknown): number | null {
  if (!dob) return null;
  const d = dob instanceof Date ? dob : new Date(dob as any);
  if (isNaN(d.getTime())) return null;

  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;   // birthday not reached this year
  return age >= 0 ? age : null;
}

/**
 * The date-of-birth window that keeps a member inside an age band, so an ineligible date can be
 * greyed out in the calendar rather than accepted and then rejected.
 *
 * `max` = the LATEST birth date that still reaches `ageFrom` completed years (and never later
 * than today). `min` = the EARLIEST that stays within `ageTo`: someone born exactly ageTo+1 years
 * ago is already a year too old, so the window opens the day after that.
 *
 * A zero bound means "unbounded", matching how the server reads the band.
 */
export function dobWindow(ageFrom: number, ageTo: number): { min?: Date; max: Date } {
  const today = new Date();
  const shiftYears = (years: number) =>
    new Date(today.getFullYear() - years, today.getMonth(), today.getDate());

  const max = ageFrom > 0 ? shiftYears(ageFrom) : today;

  let min: Date | undefined;
  if (ageTo > 0) {
    min = shiftYears(ageTo + 1);
    min.setDate(min.getDate() + 1);
  }
  return { min, max };
}

/** Fallback gender options when no dropdown API is wired. */
export const GENDER_OPTIONS = ['Male', 'Female', 'Other'];

/** Default nomination percentage for a fresh nominee row. */
export const DEFAULT_NOMINATION_PERCENTAGE = 100;
